import CollectionApi from 'client/api/v2/CollectionApi';
import axios from 'axios';

class ApplicationApi extends CollectionApi {
  constructor() {
    super('/api/applications', {
      skip: 0,
      limit: 50,
      sort: 'displayLabel', // Prepend with a '-' to reverse default sort direction
      search: {
        fields: ['name', 'displayName', 'displayLabel'],
        searchTerm: '',
      },
    });
  }

  async fetchApplicationsByName(appName) {
    const filter = [{
      name: 'name',
      operator: 'eq',
      value: appName,
    }];

    try {
      const response = await this.fetch(0, 10, 'displayLabel', '', null, filter, null, null);

      return response.results;
    } catch (e) {
      const fetchErrorMessage = 'Could not fetch applications. Please try again.';
      throw new Error(fetchErrorMessage, e);
    }
  }

  fetchUserGroups(id, params = { skip: 0, limit: 50 }) {
    const boundUserGroupsUrl = `/api/v2/applications/${id}/usergroups`;
    return this.initialize()
      .then(() => axios.get(boundUserGroupsUrl, params))
      .then(({ data }) => data);
  }

  postSPMetadata(xml, model) {
    const mesg = 'Something went wrong - the service provider metadata file could not be read';
    const data = { spConfiguration: model, spMetadata: xml };

    return this.initialize()
      .then(() => axios.post('/api/applications/metadata', data))
      .then((resp) => {
        if (resp && resp.data) return resp.data;
        throw new Error(`no data in response: ${resp}`);
      })
      .catch(() => {
        throw new Error(mesg);
      });
  }

  /**
   * Deleting application custom logo
   * @param {ObjectId} id Application Id
   */
  deleteCustomLogo(id) {
    return this
      .initialize()
      .then(() => axios.delete(`/api/v2/applications/${id}/logo`, {}));
  }

  /**
   * Endpoint used to update the OAuth 2.0 configuration
   * for Custom REST API OAuth 2.0 integrations
   *
   * Used when:
   * 1. Updating OAuth config and clicking on the authorize button
   * after a successful first auth (haven't activated integration yet)
   * 2. Updating OAuth config and clicking on the update button for an active
   * Custom REST OAuth integration
   * @param {string} id
   * @param {*} payload
   */
  idmPostReactivate(id, payload) {
    return this
      .initialize()
      .then(() => axios.post(`/api/v2/applications/${id}/provision/reactivate`, payload));
  }

  /**
   * Uploading application custom logo
   * @param {ObjectId} id Application Id
   * @param {ObjectId} file Application Custom Logo
   */
  uploadCustomLogo({ id, file }) {
    const formData = new FormData();
    formData.append('image', file);

    return this
      .initialize()
      .then(() => axios.post(`/api/v2/applications/${id}/logo`, formData));
  }

  /**
   * Application provisioning using SCIM.
   * Payload depends on the type of Idm integration
   * Its format is defined by ApplicationModel
   * @linkcode **ApplicationModel.getConfigureSCIMPayload**
   * @param {String} id Application's id
   * @param {Object} payload Payload to send to endpoint
   */
  SCIMPostProvisionActivate(id, payload) {
    return this
      .initialize()
      .then(() => axios.post(`/api/v2/applications/${id}/provision/activate`, payload));
  }

  /**
   * Update Application provisioning using SCIM
   * @param {ObjectId} id Application Id
   * @param {ObjectId} state server state id
   * @param {String} vanityUrl Service provider vanity URL
   */
  SCIMPutProvisionActivate(id, state, vanityUrl = '') {
    return this
      .initialize()
      .then(() => axios.put(`/api/v2/applications/${id}/provision/activate`, {
        state,
        vanity_url: vanityUrl,
      }));
  }

  /**
   * Application provisioned using SCIM
   * @param {ObjectId} id Application Id
   */

  /**
   * NOTE: adding a succesful status code range as some reqs are returning 200
   * while other reqs made to cached resources are returning 300 range status codes
   */
  SCIMGetProvisionActivate(id) {
    return this
      .initialize()
      .then(() => axios.get(`/api/v2/applications/${id}/provision`, {
        validateStatus: status => status >= 200 && status <= 399,
      }));
  }

  /**
   * Application provisioned using SCIM
   */
  SCIMPutProvision(id, payload = {}) {
    const { base_url: baseUrl = null } = payload;
    const sanitizedPayload = {
      ...payload,
      base_url: baseUrl === '' ? null : baseUrl,
    };
    return this
      .initialize()
      .then(() => axios.put(`/api/v2/applications/${id}/provision`, sanitizedPayload));
  }

  /**
   * Deactivate SCIM for Application
   * @param {ObjectId} id Application Id
   */
  SCIMDeleteProvisionActive(id) {
    return this
      .initialize()
      .then(() => axios.delete(`/api/v2/applications/${id}/provision`, {}));
  }

  /**
   * Finish SCIM activation for Application
   * @param {Object} Options
   * @param {String} Options.apiKey API Key from SP
   * @param {String} Options.appName Application template name
   * @param {String} Options.state Id of flow state started by SCIMPostProvisionActivate
   * @param {String} Options.vanityUrl Vanity URL from the SP
   */
  SCIMPostProvisionCallback({
    apiKey = '',
    appName,
    groupsEnabled = false,
    state,
    vanityUrl = '',
    clientId,
    clientSecret,
  }) {
    let payload = {
      api_key: apiKey ? apiKey.trim() : '',
      groups_enabled: groupsEnabled,
      state,
      vanity_url: vanityUrl ? vanityUrl.trim() : '',
    };

    if (clientId && clientSecret) {
      payload = {
        state,
        client_id: clientId,
        client_secret: clientSecret,
        groups_enabled: false,
      };
    }

    return this
      .initialize()
      .then(() => axios.post(`/api/v2/provision/${appName}/callback`, payload));
  }

  /**
   * Test credentials for SCIM generic connector
   * @param {Object} payload Formatted payload for BE
   */
  SCIMPostVerify(payload) {
    return this
      .initialize()
      .then(() => axios.post('/api/v2/provision/client/verify', payload));
  }

  idmGetConfig(appName) {
    return this
      .initialize()
      .then(() => axios.get(`/api/v2/provision/${appName}/config`));
  }
}

export default new ApplicationApi();
